import { useCallback, useRef, useState, type ReactNode } from "react";
import { IconX } from "./admin/icons";
import { useDialogFocus } from "./admin/useDialogFocus";
import "./admin/members.css";

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** A destructive action: the confirm button is red. */
  danger?: boolean;
}

function ConfirmDialog({ options, onDone }: { options: ConfirmOptions; onDone: (ok: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // Escape and the backdrop answer "no"; focus starts on the safe choice.
  useDialogFocus(ref, () => onDone(false), cancelRef);
  return (
    <div className="modal-backdrop" onClick={() => onDone(false)}>
      <div ref={ref} className="modal confirm-modal" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true" aria-label={options.title}>
        <div className="modal-head">
          <h3>{options.title}</h3>
          <button className="sheet-close" onClick={() => onDone(false)} aria-label="Close">
            <IconX />
          </button>
        </div>
        {options.body && <div className="modal-body confirm-body">{options.body}</div>}
        <div className="modal-foot">
          <span className="spacer" />
          <button ref={cancelRef} className="btn" onClick={() => onDone(false)}>{options.cancelLabel ?? "Cancel"}</button>
          <button className={`btn ${options.danger ? "btn-danger" : "btn-accent"}`} onClick={() => onDone(true)}>{options.confirmLabel ?? "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The app's own confirm dialog instead of window.confirm:
 *   const [confirm, dialog] = useConfirm();
 *   if (await confirm({ title: "Delete it?", danger: true })) ...
 *   return <>{dialog}...</>;
 */
export function useConfirm(): [(options: ConfirmOptions) => Promise<boolean>, ReactNode] {
  const [pending, setPending] = useState<{ options: ConfirmOptions; resolve: (ok: boolean) => void } | null>(null);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ options, resolve })), []);
  const dialog = pending ? (
    <ConfirmDialog
      options={pending.options}
      onDone={(ok) => {
        pending.resolve(ok);
        setPending(null);
      }}
    />
  ) : null;
  return [confirm, dialog];
}
